import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { CoreContext } from "context/CoreContext";
import { ReadOnboardingVideoProgress, UpdateMe, WELCOME_ONBOARDING_VIDEO_KEY } from "services/me";
import { SaveObject } from "services/storage";
import { apiRequest, exposeStrapiError, parseStrapiImage } from "utils";
import { ReadDashboardHome } from "services/dashboardHome";
import { languages } from "utils/options";

export default function useController() {
    const history = useHistory();

    const { setFiltered, setModal, user, reloadMe, language } = useContext(CoreContext);
    const locale = useMemo(() => languages.find((item) => item.id === language)?.ref || 'en', [language]);
    const welcomeCheckUserRef = useRef(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        const result = await apiRequest(() => ReadDashboardHome(locale));
        if (result && !exposeStrapiError(result)) {
            setData(result?.data || null);
        }
        setLoading(false);
    }, [locale]);

    useEffect(() => {
        load();
    }, [load]);

    const handleFirstLogin = useCallback(async () => {
        await apiRequest(() => UpdateMe({ first_login: false }));
        await reloadMe();
    }, [reloadMe]);

    useEffect(() => {
        if (!user?.first_login) return;
        if (welcomeCheckUserRef.current === user?.id) return;

        welcomeCheckUserRef.current = user?.id;

        const checkWelcomeProgress = async () => {
            const result = await apiRequest(() => ReadOnboardingVideoProgress(WELCOME_ONBOARDING_VIDEO_KEY));
            if (result && !exposeStrapiError(result) && result?.completed) return;

            setModal({
                type: "welcome",
                videoKey: WELCOME_ONBOARDING_VIDEO_KEY,
                progress: result && !result?.error ? result : null,
            });
            handleFirstLogin();
        };

        checkWelcomeProgress();
    }, [handleFirstLogin, setModal, user]);

    useEffect(() => {
        setFiltered(null);
        SaveObject("filtered", null);
    }, [setFiltered]);


    const handleAction = useCallback((action) => {
        if (!action?.type || !action?.value) return;

        if (action.type === "navigate") {
            if (`${action.value}`.startsWith("http")) {
                window.open(action.value, "_blank", "noopener,noreferrer");
                return;
            }

            history.push(action.value);
            return;
        }

        if (action.type === "download") {
            window.location.href = parseStrapiImage(action.value);
        }
    }, [history]);

    const hero = {
        eyebrow: data?.hero?.eyebrow || "",
        title: data?.hero?.title || "",
        description: data?.hero?.description || "",
        ctaLabel: data?.hero?.ctaLabel || "",
        ctaUrl: data?.hero?.ctaUrl || "",
        imageUrl: data?.hero?.imageUrl ? parseStrapiImage(data.hero.imageUrl) : null,
    };

    return {
        loading,
        hero,
        sections: data?.sections || [],
        handleAction,
    };
}
